import { useMemo, useState, type FC } from 'react';

import { SidebarItem } from '@mint-lib/tw-components/patterns';
import type { MediaPlan } from '@mint-sdk/api-media-plan';
import { getRouteApi } from '@mint/routing';

import { MP_LEVELS } from '../../../consts/index.js';
import { levelIcons } from '../../../consts/sidebar.js';
import { useChosenLevel } from '../../../hooks/useChosenLevel.js';
import { chooseLevel } from '../../../utils/chooseLevel.js';
import { getDefaultExpand } from '../../../utils/sidebar.js';
import SidebarItems from './SidebarItems.jsx';

type Props = {
  sidebarTree: MediaPlan.SidebarItem;
};
const routerApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const SidebarList: FC<Props> = ({ sidebarTree }) => {
  const navigate = routerApi.useNavigate();

  const [expandOverrides, setExpandOverrides] = useState<
    Record<string, boolean>
  >({});

  const {
    mediaDataId: planMediaDataId,
    subItems: mediaPlanSubItems,
    name: mediaPlanLabel,
  } = sidebarTree;

  const { id: chosenId } = useChosenLevel();

  const defaultExpandState = useMemo(() => {
    return {
      [planMediaDataId]: !!mediaPlanSubItems.length,
      ...getDefaultExpand(mediaPlanSubItems),
    };
  }, [mediaPlanSubItems, planMediaDataId]);

  const expandedState = useMemo(() => {
    return { ...defaultExpandState, ...expandOverrides };
  }, [expandOverrides, defaultExpandState]);

  const toggleSubMenu = (mediaDataId: string) => {
    setExpandOverrides({
      ...expandedState,
      [mediaDataId]: !expandedState[mediaDataId],
    });
  };

  const handleItemClick = async (item: MediaPlan.SidebarItem) => {
    const { mediaDataId, subItems } = item;
    const isClickedToChosen = chosenId === mediaDataId;
    const hasSubItems = subItems && subItems.length > 0;

    if (hasSubItems && isClickedToChosen) {
      toggleSubMenu(mediaDataId);
      return;
    }

    if (!isClickedToChosen) {
      navigate({
        search: (search) => chooseLevel(item, search),
      });
    }
  };

  const MediaPlanIcon = levelIcons[MP_LEVELS.MEDIA_PLAN];

  return (
    <>
      <SidebarItem
        variant="primary"
        text={mediaPlanLabel}
        expandable={!!mediaPlanSubItems.length}
        icon={<MediaPlanIcon />}
        onClick={() => handleItemClick(sidebarTree)}
        onChevronClick={() => toggleSubMenu(planMediaDataId)}
        open={expandedState[planMediaDataId]}
        selected={planMediaDataId === chosenId}
        data-dd-action-name="Planning sidebar: open media plan"
      />
      {expandedState[planMediaDataId] && !!mediaPlanSubItems.length && (
        <SidebarItems
          items={mediaPlanSubItems}
          isExpanded={expandedState}
          handleItemClick={handleItemClick}
          toggleSubMenu={toggleSubMenu}
        />
      )}
    </>
  );
};

export default SidebarList;
